import React, { useState, useMemo, useContext, useCallback, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { TryItColumns, TryItRows } from "../../Data_Sets/TryItTableData.js";
import { Button, TextField } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import "../../Styles/Tables/UserInputTable.css";
import { DetermineWhichCellsToHighlight } from "../../Utils/DetermineWhichCellsToHighlight.js";
import { AppContext } from "../../Utils/Context.js";

const UserInputTable = ({generatedData}) => {
  const STORAGE_KEY = "TryItData";
  const { subscribedTopicString, setSubscribedTopicString, setColumnData } = useContext(AppContext);

  const [data, setData] = useState(() => {
  if (generatedData) {
    return { columns: generatedData.columns, rows: generatedData.rows };
  } else {
    const savedData = localStorage.getItem(STORAGE_KEY);
    return savedData ? JSON.parse(savedData) : { columns: TryItColumns, rows: TryItRows };
  }
});

  const [gridKey, setGridKey] = useState("0");
  const { columns, rows } = data;

  useEffect(() => {
    if (generatedData) {
      setData({ columns: generatedData.columns, rows: generatedData.rows });
    }
  }, [generatedData]);

  const updateLocalStorage = (updatedData) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
  };

  const tableUpdated = (updatedRow) => {
    if (!updatedRow?.id) {
      console.error("Invalid updated row: Missing ID");
      return;
    }

    const updatedRows = rows.map(row =>
      row.id === updatedRow.id ? { ...row, ...updatedRow } : row
    );

    const updatedData = { columns, rows: updatedRows };
    setData(updatedData);
    updateLocalStorage(updatedData);
    setGridKey(prevKey => prevKey + 1);
  };

  const handleClear = () => {
    localStorage.removeItem(STORAGE_KEY);
    setData({ columns: TryItColumns, rows: TryItRows });
    setSubscribedTopicString("");
    setGridKey(prevKey => prevKey + 1);
  };

  const handleAddRow = () => {
    const newRowId = rows.length > 0 ? Math.max(...rows.map(row => row.id)) + 1 : 1;
    const newRow = { id: newRowId };

    columns.forEach(column => {
      newRow[column.field] = ''; 
    });

    const updatedRows = [...rows, newRow];
    const updatedData = { columns, rows: updatedRows };
    setData(updatedData);
    updateLocalStorage(updatedData);
  };

  const handleAddColumn = () => {
    const newColumnIndex = columns.length + 1;
    const newColumn = {
      field: `level-${newColumnIndex}`,
      headerName: `Level-${newColumnIndex}`,
      width: 180,
      editable: true,
      sortable: false,
    };

    const updatedColumns = [...columns, newColumn];
    const updatedRows = rows.map(row => ({
      ...row,
      [`level-${newColumnIndex}`]: '',
    }));

    const updatedData = { columns: updatedColumns, rows: updatedRows };
    setData(updatedData);
    updateLocalStorage(updatedData);
  };

  const getRowId = (row) => row?.id ?? null;

  const memoizedColumnData = useMemo(() => {
    return columns.map(column => {
      const columnKey = column.field;
      const availableItems = rows
        .map(row => row[columnKey])
        .filter(item => item !== undefined && item !== "");

      return { columnKey, columnData: availableItems };
    });
  }, [columns, rows]);

  useEffect(() => {
    setColumnData(memoizedColumnData);
  }, [memoizedColumnData, setColumnData]);

  const getCellClassName = useCallback((params) => {
    if (!params.value) return "";
    return DetermineWhichCellsToHighlight(subscribedTopicString, memoizedColumnData, params);
  }, [subscribedTopicString, memoizedColumnData]);

  const handleHeaderChange = useCallback((field, newHeader) => {
    const updatedColumns = columns.map(col => 
      col.field === field ? { ...col, headerName: newHeader } : col
    );
  
    const updatedData = { columns: updatedColumns, rows };
    setData(updatedData);
    updateLocalStorage(updatedData);
  }, [columns, rows]);

  const customColumns = useMemo(() => {
    return columns.map((column, index) => ({
      ...column,
      cellClassName: (params) => {
        return getCellClassName({ ...params, columnIndex: index });
      },
      renderHeader: (params) => (
        <EditableHeader column={column} onHeaderChange={handleHeaderChange} />
      ),
    }));
  }, [columns, getCellClassName, handleHeaderChange]);


  return (
    <div>
      <div className="try-it-table-container">
        <div className="try-it-button-container">
          <Button
            variant="outlined"
            sx={{ width: "6rem", height: "2rem", fontSize: "var(--small-font-size)", color: "red", textTransform: "none", borderColor: "red", fontFamily: "Arial" }}
            onClick={handleClear}>
              <DeleteIcon /> Clear
          </Button>
          
          <Button
            variant="outlined"
            sx={{ width: "6rem", height: "2rem", fontSize: "var(--small-font-size)", textTransform: "none", fontFamily: "Arial" }}
            onClick={handleAddRow}>
              Add Row
          </Button>

          <Button
            variant="outlined"
            sx={{ width: "8rem", height: "2rem", fontSize: "var(--small-font-size)", textTransform: "none", fontFamily: "Arial" }}
            onClick={handleAddColumn}>
              Add Column
          </Button>
        </div>
        <DataGrid
          key={gridKey}
          rows={rows}
          columns={customColumns}
          getRowId={getRowId}
          processRowUpdate={row => {
            tableUpdated(row);
            return row; }}          
            onProcessRowUpdateError={(params) => console.error("Error updating row:", params)}
          sx={{fontFamily: "Arial"}}
        />
      </div>
    </div>
  );
};

const EditableHeader = ({ column, onHeaderChange }) => {
  const [headerText, setHeaderText] = useState(column.headerName);

  const handleChange = (event) => {
    setHeaderText(event.target.value);
  };

  const handleBlur = () => {
    onHeaderChange(column.field, headerText);
  };

  return (
    <TextField
      value={headerText}
      onChange={handleChange}
      onBlur={handleBlur}
      variant="standard"
      size="small"
      InputProps={{
        disableUnderline: true,
        style: {
          fontSize: 'inherit',
          fontFamily: 'inherit',
        },
      }}
    />
  );
};


export default UserInputTable;
